import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { MatchData } from '../_interfaces';
import { v4 } from 'uuid';
import { LocalStorageService } from '.';
import { BehaviorSubject } from 'rxjs';
import { bleachrHTTP } from '../_classes/http';
import { ToastrService } from 'ngx-toastr';
import { SsrlocalstoreService } from './ssr-localstore.service';

export interface FollowPlayerPayload {
    id: string,
    account_id: string,
    fan_id: string,
    golfer_ids: string[]
}

@Injectable({
    providedIn: 'root'
})
export class FollowedPlayerService {
    public followed_players: BehaviorSubject<string[]> = new BehaviorSubject<string[]>([])

    constructor(
        private http: bleachrHTTP,
        private lStore: LocalStorageService,
        public toast: ToastrService,
        private ssr: SsrlocalstoreService
    ) { }

    async initFollowed() {
        if (this.ssr.isLoggedIn()) {
            this.followed_players.next(await this.getFollowedPlayersIds());
        }
        return
    }

    async getFollowedPlayerRecentMatch(): Promise<any> {
        return this.http.get<{ data: MatchData[], error: string }>(`${environment.ELIXIR_API_FOUR}/golf/players/recent_match?followed=true&simplified_draw_type=S`, { type: 'auth' })
            .then((res) => {
                return res.data;
            })
            .catch((error) => {
                console.error(error)
                return null;
            });
    }

    private async getFollowedPlayersIds(): Promise<string[]> {
        try {
            return this.http.get<{ data: string[], error: string }>(`${environment.ELIXIR_API_FOUR}/golf/players/followed_players`, { type: 'auth' })
                .then((res) => {
                    return res.data;
                })
                .catch((err) => {
                    console.error(err)
                    return []
                })
        } catch (err) {
            throw err
        }
    }

    async getFollowedPlayers(): Promise<any> {
        if (!this.ssr.isLoggedIn()) {
            return Promise.resolve([])
        }
        try {
            return this.http.get<{ data: string[], error: string }>(`${environment.ELIXIR_API_FOUR}/my_followed_players`, { type: 'auth' })
                .then((res) => {
                    return res.data;
                })
                .catch((err) => {
                    console.error(err)
                    return []
                })
        } catch (err) {
            throw err
        }
    }

    async followPlayer(player_id: string): Promise<any> {
        const payload: FollowPlayerPayload = {
            id: v4(),
            account_id: environment.GOLF_ONE_ACCOUNT_ID,
            fan_id: this.lStore.getLocalStore().user.fan_id,
            golfer_ids: [...new Set([...this.followed_players.value, player_id])]
        }

        return this.http.post<{ data: string, error: string }, FollowPlayerPayload>(`${environment.ELIXIR_API_FOUR}/golf/players/bulk_follow_players`, payload, { type: 'auth' })
            .then((res) => {
                // this returns an empty string
                this.updateFollowed(payload.golfer_ids)
                return res;
            })
            .catch((err) => {
                throw err;
            })
    }

    async unfollowPlayer(player_id: string) {
        const payload: { golfer_id: string } = {
            golfer_id: player_id
        }
        const headers = this.http.authHeaders()
        return this.http.post<{ data: any, error: string }, { golfer_id: string }>(`${environment.ELIXIR_API_FOUR}/golf/players/unfollow`, payload, { type: 'auth' })
            .then((res) => {
                // this returns an empty string
                const update = this.followed_players.value.filter((id => id !== player_id))
                this.updateFollowed(update)
                return res;
            })
            .catch((err) => {
                throw err;
            })
    }

    private updateFollowed(updated: string[]) {
        this.followed_players.next(updated)

    }

    isFollowed(player_id: string | undefined | null): boolean {
        if (!player_id) return false;
        return this.followed_players.value.includes(player_id);
    }

    toggleFollow(player_id: string | undefined | null): void {
        if (!player_id) return;
        if (!this.ssr.isLoggedIn()) {
            this.toast.warning('Please sign in to follow players.');
            return;
        }

        if (this.isFollowed(player_id)) {
            this.updateFollowed(this.followed_players.value.filter(id => id !== player_id));
            this.unfollowPlayer(player_id).catch(() => this.initFollowed());
        } else {
            this.updateFollowed([...this.followed_players.value, player_id]);
            this.followPlayer(player_id).catch(() => this.initFollowed());
        }
    }
}
